import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';

export interface Product {
  id: string;
  code: string;
  description: string;
  arDescription?: string;
  name: string;
  nameEn: string;
  nameAr?: string;
  unitCode: string;
  categoryCode?: string;
  pricing: {
    basePrice: number;
    lastPurchasePrice?: number;
  };
  barcodes: string[];
  inventory: {
    minQty: number;
    maxQty: number;
    criticalQty: number;
  };
  isRecipe: boolean;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateProductData {
  code: string;
  description: string;
  arDescription?: string;
  unitCode: string;
  categoryCode?: string;
  pricing: {
    basePrice: number;
    lastPurchasePrice?: number;
  };
  barcodes?: string[];
  inventory?: {
    minQty?: number;
    maxQty?: number;
    criticalQty?: number;
  };
  isRecipe?: boolean;
}

export interface UpdateProductData {
  description?: string;
  arDescription?: string;
  unitCode?: string;
  categoryCode?: string;
  pricing?: {
    basePrice: number;
    lastPurchasePrice?: number;
  };
  barcodes?: string[];
  inventory?: {
    minQty?: number;
    maxQty?: number;
    criticalQty?: number;
  };
  isRecipe?: boolean;
  isActive?: boolean;
}

interface ProductsResponse {
  data: Product[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface ProductsParams {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  isActive?: boolean;
}

export const useProducts = (params: ProductsParams = {}) => {
  const { page = 1, limit = 20, search, category, isActive } = params;

  return useQuery({
    queryKey: ['products', { page, limit, search, category, isActive }],
    queryFn: () =>
      apiClient.get<ProductsResponse>('/products', {
        page,
        limit,
        search,
        category,
        isActive,
      }),
  });
};

export const useProduct = (id: string) => {
  return useQuery({
    queryKey: ['product', id],
    queryFn: () => apiClient.get<Product>(`/products/${id}`),
    enabled: !!id,
  });
};

export const useProductByCode = (code: string) => {
  return useQuery({
    queryKey: ['product', 'code', code],
    queryFn: () => apiClient.get<Product>(`/products/code/${code}`),
    enabled: !!code,
  });
};

export const useProductByBarcode = (barcode: string) => {
  return useQuery({
    queryKey: ['product', 'barcode', barcode],
    queryFn: () => apiClient.get<Product>(`/products/barcode/${barcode}`),
    enabled: !!barcode,
  });
};

export const useSearchProducts = (query: string, limit: number = 10) => {
  return useQuery({
    queryKey: ['products', 'search', query],
    queryFn: () =>
      apiClient.get<{ data: Product[] }>('/products/search', { q: query, limit }),
    enabled: query.length >= 2,
  });
};

export const useCreateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateProductData) =>
      apiClient.post<Product>('/products', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
};

export const useUpdateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProductData }) =>
      apiClient.patch<Product>(`/products/${id}`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', variables.id] });
    },
  });
};

export const useDeleteProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/products/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
};
